import { useEffect, useState, type FormEvent } from 'react';
import type { UserProfileStats } from '@xiangqi/protocol';
import { adoptTokens, api, currentUser, ensureSession } from '../api.ts';
import { clearSession } from '../session.ts';

interface ProfileScreenProps {
  navigate(path: string): void;
}

export function ProfileScreen({ navigate }: ProfileScreenProps) {
  const [stats, setStats] = useState<UserProfileStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [account, setAccount] = useState<{ user_id: string; display_name: string; username?: string | null; kind: 'guest' | 'account' } | null>(() => currentUser());
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [displayNameInput, setDisplayNameInput] = useState('');
  const [accountMode, setAccountMode] = useState<'register' | 'login'>('register');
  const [accountBusy, setAccountBusy] = useState(false);
  const [accountError, setAccountError] = useState<string | null>(null);
  const user = currentUser();

  useEffect(() => {
    let cancelled = false;
    ensureSession()
      .then(async () => Promise.all([api.stats(), api.me()]))
      .then(([res, profile]) => {
        if (!cancelled) {
          setStats(res.stats);
          if (profile.user) setAccount(profile.user);
          setLoading(false);
        }
      })
      .catch(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  const handleLogout = () => {
    clearSession();
    window.location.href = '/';
  };

  const handleAccountSubmit = async (event: FormEvent) => {
    event.preventDefault();
    setAccountBusy(true); setAccountError(null);
    try {
      const tokens = accountMode === 'register'
        ? await api.register(username.trim(), password, displayNameInput.trim() || undefined)
        : await api.login(username.trim(), password);
      adoptTokens(tokens);
      const result = await api.me();
      setAccount(result.user ?? tokens.user);
      setPassword('');
    } catch (error) {
      setAccountError(error instanceof Error ? error.message : 'Không thể xác thực tài khoản.');
    } finally { setAccountBusy(false); }
  };

  const handleDeleteAccount = async () => {
    setDeleting(true);
    setDeleteError(null);
    try {
      await api.deleteAccount();
      clearSession();
      window.location.href = '/';
    } catch (e) {
      setDeleteError(e instanceof Error ? e.message : 'Xóa tài khoản thất bại');
      setDeleting(false);
    }
  };

  const displayName = stats?.display_name ?? user?.display_name ?? 'Kỳ thủ';
  const userId = stats?.user_id ?? user?.user_id ?? '000000';
  const totalGames = stats?.total_games ?? 0;
  const wins = stats?.wins ?? 0;
  const losses = stats?.losses ?? 0;
  const draws = stats?.draws ?? 0;
  const winRate = stats?.win_rate ?? 0;
  const level = stats?.level ?? 1;
  const rank = stats?.rank ?? 1;

  return (
    <main className="page profile-page">
      <header className="page-header">
        <h1>👤 Trang cá nhân</h1>
      </header>

      {loading ? (
        <p className="waiting">Đang tải thông tin…</p>
      ) : (
        <div className="profile-container">
          {/* Profile Card matching Figma */}
          <div className="profile-hero-card">
            <div className="profile-avatar">
              <span className="avatar-piece">帥</span>
            </div>
            <div className="profile-hero-info">
              <h2>{displayName}</h2>
              <div className="profile-badges">
                <span className="level-badge">Cấp độ {level}</span>
                <span className="rank-badge">Hạng #{rank}</span>
              </div>
              <p className="profile-id">ID: {userId.slice(0, 8)}</p>
            </div>
          </div>

          {/* Stats Grid matching Figma frame 6:9 */}
          <div className="stats-grid">
            <div className="stat-card win-stat">
              <span className="stat-label">Ván Thắng</span>
              <strong className="stat-value">{wins}</strong>
              <span className="stat-sub">Tỷ lệ: {winRate}%</span>
            </div>

            <div className="stat-card loss-stat">
              <span className="stat-label">Ván Thua</span>
              <strong className="stat-value">{losses}</strong>
              <span className="stat-sub">ván</span>
            </div>

            <div className="stat-card draw-stat">
              <span className="stat-label">Ván Hòa</span>
              <strong className="stat-value">{draws}</strong>
              <span className="stat-sub">ván</span>
            </div>

            <div className="stat-card total-stat">
              <span className="stat-label">Tổng số ván</span>
              <strong className="stat-value">{totalGames}</strong>
              <span className="stat-sub">trận đã đấu</span>
            </div>
          </div>

          {/* Quick Action Navigation */}
          <div className="profile-actions-list">
            {account?.kind === 'guest' ? <section className="profile-account-card">
              <h2>Lưu danh tính và kết nối bạn bè</h2>
              <p>Tạo tài khoản để đăng nhập lại trên thiết bị khác và giữ danh sách bạn bè, tin nhắn.</p>
              <div className="profile-account-tabs"><button type="button" className={accountMode === 'register' ? 'active' : ''} onClick={() => { setAccountMode('register'); setAccountError(null); }}>Tạo tài khoản</button><button type="button" className={accountMode === 'login' ? 'active' : ''} onClick={() => { setAccountMode('login'); setAccountError(null); }}>Đăng nhập</button></div>
              <form onSubmit={(event) => void handleAccountSubmit(event)}>
                <label>Tên đăng nhập<input autoComplete="username" minLength={3} maxLength={24} required value={username} onChange={(event) => setUsername(event.target.value)} /></label>
                <label>Mật khẩu<input autoComplete={accountMode === 'register' ? 'new-password' : 'current-password'} type="password" minLength={8} maxLength={72} required value={password} onChange={(event) => setPassword(event.target.value)} /></label>
                {accountMode === 'register' && <label>Tên hiển thị<input maxLength={32} value={displayNameInput} onChange={(event) => setDisplayNameInput(event.target.value)} placeholder={username || 'Tên của bạn'} /></label>}
                {accountError && <p className="social-alert" role="status">{accountError}</p>}
                <button className="social-primary" disabled={accountBusy}>{accountBusy ? 'Đang xử lý…' : accountMode === 'register' ? 'Tạo tài khoản' : 'Đăng nhập'}</button>
              </form>
            </section> : <p className="profile-account-status">Tài khoản @{account?.username ?? username} đã sẵn sàng để kết nối bạn bè.</p>}
            <button
              className="profile-nav-item"
              onClick={() => navigate('/history')}
            >
              <span className="item-icon">📜</span>
              <span className="item-text">Lịch sử đấu</span>
              <span className="item-arrow">→</span>
            </button>

            <button
              className="profile-nav-item"
              onClick={() => navigate('/leaderboard')}
            >
              <span className="item-icon">🏆</span>
              <span className="item-text">Bảng xếp hạng</span>
              <span className="item-arrow">→</span>
            </button>

            <button
              className="profile-nav-item"
              onClick={() => navigate('/privacy')}
            >
              <span className="item-icon">⚖️</span>
              <span className="item-text">Chính sách & Quyền riêng tư</span>
              <span className="item-arrow">→</span>
            </button>

            <button
              className="profile-nav-item logout"
              onClick={handleLogout}
            >
              <span className="item-icon">🚪</span>
              <span className="item-text">Đăng xuất</span>
            </button>

            <button
              className="profile-nav-item delete-account-btn"
              onClick={() => setShowDeleteConfirm(true)}
            >
              <span className="item-icon">🗑️</span>
              <span className="item-text">Xóa tài khoản</span>
            </button>
          </div>

          {/* Delete Account Confirmation Modal */}
          {showDeleteConfirm && (
            <div
              className="modal-backdrop"
              onClick={(e) => {
                if (e.target === e.currentTarget && !deleting) setShowDeleteConfirm(false);
              }}
            >
              <div className="modal" role="dialog" aria-modal="true">
                <h2 style={{ color: 'var(--red)' }}>⚠️ Xác nhận xóa tài khoản</h2>
                <p style={{ fontSize: '14px', lineHeight: '1.4', color: 'var(--ink)' }}>
                  Bạn có chắc chắn muốn xóa tài khoản này không? Mọi thông tin đăng nhập, hồ sơ cá nhân, danh sách bạn bè và tin nhắn của bạn sẽ bị xóa vĩnh viễn. Biên bản và kết quả ván đấu được giữ lại để bảo đảm lịch sử trận và sẽ được ẩn danh dưới tên &quot;Tài khoản đã xóa&quot;.
                </p>
                {deleteError && <p className="error">{deleteError}</p>}
                <div style={{ display: 'flex', gap: '10px', marginTop: '10px' }}>
                  <button
                    className="danger"
                    style={{ flex: 1 }}
                    disabled={deleting}
                    onClick={() => void handleDeleteAccount()}
                  >
                    {deleting ? 'Đang xóa…' : 'Xác nhận xóa'}
                  </button>
                  <button
                    style={{ flex: 1 }}
                    disabled={deleting}
                    onClick={() => setShowDeleteConfirm(false)}
                  >
                    Hủy bỏ
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </main>
  );
}
